import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import FadeInSection from '@/components/base/FadeInSection';

interface GalleryImage {
  src: string;
  alt: string;
  category: string;
}

const categories = [
  { id: 'all', label: 'All' },
  { id: 'vegetables', label: 'Fresh Vegetables' },
  { id: 'fruits', label: 'Fresh Fruits' },
  { id: 'packing', label: 'Packing & Sorting' },
  { id: 'supply', label: 'Supply Process' },
];

const galleryImages: GalleryImage[] = [
  { src: '/images/gallery-vegetables-1.jpg', alt: 'Fresh vegetables in crates', category: 'vegetables' },
  { src: '/images/gallery-vegetables-2.jpg', alt: 'Colorful fresh vegetables', category: 'vegetables' },
  { src: '/images/gallery-vegetables-3.jpg', alt: 'Vegetable supply warehouse', category: 'vegetables' },
  { src: '/images/gallery-fruits-1.jpg', alt: 'Tropical fruits in boxes', category: 'fruits' },
  { src: '/images/gallery-fruits-2.jpg', alt: 'Apples and citrus fruits', category: 'fruits' },
  { src: '/images/gallery-fruits-3.jpg', alt: 'Seasonal fruits in crates', category: 'fruits' },
  { src: '/images/gallery-packing-1.jpg', alt: 'Sorting and packing vegetables', category: 'packing' },
  { src: '/images/gallery-packing-2.jpg', alt: 'Quality inspection process', category: 'packing' },
  { src: '/images/gallery-supply-1.jpg', alt: 'Produce loaded for delivery', category: 'supply' },
  { src: '/images/gallery-supply-2.jpg', alt: 'Bulk vegetable supply warehouse', category: 'supply' },
  { src: '/images/gallery-supply-3.jpg', alt: 'B2B produce supply ready', category: 'supply' },
];

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filtered = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const goPrev = () => {
    setLightboxIndex((prev) => (prev === 0 ? filtered.length - 1 : prev - 1));
  };

  const goNext = () => {
    setLightboxIndex((prev) => (prev === filtered.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <InnerPageHero
          bgImage="/images/gallery-hero.jpg"
          bgAlt="Bassi Enterprises gallery - fresh produce supply"
          bgTitle="Bassi Enterprises - Our Gallery | Fresh Produce Supply"
          breadcrumbLabel="Gallery"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-image-line"></i>
              Visual Tour
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Our Gallery
            </h1>
            <p className="text-background-200 text-base md:text-lg leading-relaxed">
              Take a look at our fresh produce, packing process, and supply operations. Quality you can see — trust you can count on.
            </p>
          </div>
        </InnerPageHero>

        {/* Category Filter */}
        <section className="py-8 px-4 md:px-6 lg:px-10 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-10 md:py-16 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filtered.map((image, idx) => (
                <FadeInSection key={idx} delay={idx * 80}>
                  <div
                    onClick={() => openLightbox(idx)}
                    className="group relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-background-100 cursor-pointer"
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                        <i className="ri-zoom-in-line text-foreground-900 text-xl"></i>
                      </div>
                    </div>
                  </div>
                </FadeInSection>
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-image-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">No images found in this category.</p>
              </div>
            )}
          </div>
        </section>

        {lightboxOpen && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-background-50/10 hover:bg-background-50/20 flex items-center justify-center text-background-50 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <button
              onClick={goPrev}
              className="absolute left-4 md:left-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 hover:bg-background-50/20 flex items-center justify-center text-background-50 transition-colors cursor-pointer z-10"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>

            <div className="w-full max-w-4xl max-h-[80vh] mx-4">
              <img
                src={filtered[lightboxIndex]?.src}
                alt={filtered[lightboxIndex]?.alt}
                className="w-full h-full object-contain rounded-lg"
              />
            </div>

            <button
              onClick={goNext}
              className="absolute right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 hover:bg-background-50/20 flex items-center justify-center text-background-50 transition-colors cursor-pointer z-10"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-background-50/70 text-sm">
              {lightboxIndex + 1} / {filtered.length}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
